import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import { hasValue } from 'utils/NumberUtil'
import { CardTable } from 'components/datatable/NewTable'
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { H_PGSSE_MODULE } from 'account/constants/AccessRulesConstants'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import PgsseAction from 'pgsse/action/PgsseAction'
import useActions from 'utils/customHook/useActions'
import { pgsseIndicator } from 'pgsse/dto/PgsseShapes'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Input from 'components/forms/Input'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import { compact, isNil } from 'lodash'
import moment from 'moment'
import { getDate } from 'utils/DateUtil'
import NumberField from 'components/forms/NumberField'
import useTitle from 'utils/customHook/useTitle'

const IndicatorDialog = ({
    isOpen = false,
    setIsOpen = () => {},
    selectedIndicator = {},
    setSelectedIndicator = () => {},
    onValidate = () => {},
}) => {
    return (
        <Dialog open={isOpen}>
            <DefaultDialogTitle title={i18n.indicator} onClose={() => setIsOpen(false)}/>
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <Input
                                    title={i18n.name}
                                    value={selectedIndicator.name}
                                    onChange={v => setSelectedIndicator(prev => ({ ...prev, name: v }))}
                                />
                            </Grid>
                            <Grid size={6}>
                                <NumberField
                                    title={i18n.order}
                                    value={selectedIndicator.order}
                                    onChange={v => setSelectedIndicator(prev => ({ ...prev, order: v }))}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SimpleTextArea
                                    title={i18n.description}
                                    value={selectedIndicator.description}
                                    onChange={v => setSelectedIndicator(prev => ({ ...prev, description: v }))}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    onClick={onValidate}
                >
                    {i18n.save}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

IndicatorDialog.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    selectedIndicator: pgsseIndicator,
    setSelectedIndicator: PropTypes.func,
    onValidate: PropTypes.func,
}

const PgsseSettings = () => {
    const dispatch = useDispatch()
    const {
        accountUser = [],
        indicators: propsIndicators,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        indicators: store.PgsseReducer.indicators,
    }), shallowEqual)

    const [readMode, setReadmode] = useState(true)
    const [selectedIndicator, setSelectedIndicator] = useState({})
    const [indicators, setIndicators] = useState([])
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        dispatch(PgsseAction.fetchIndicators())
    }, [])

    useEffect(() => {
        setIndicators(propsIndicators)
    }, [propsIndicators])

    useEffect(() => {
        if (hasValue(accountUser.login) && accountUser.isAdmin === '1') {
            if (!componentHasHabilitations(H_PGSSE_MODULE)) {
                dispatch(ToastrAction.error(i18n.AccessRightDeny))
                dispatch(push('/'))
            }
        }
    }, [accountUser])

    useTitle(() => [{
        title: i18n.pgsse,
        href: 'pgsse',
    }, {
        title: i18n.settings,
        href: 'pgsse/settings',
    }], [])

    const indexedIndicators = indicators.map((d, index) => ({ ...d, index }))
    const tableData = indexedIndicators.map(ii => ({
        ...ii,
        updateDate: getDate(ii.updateDate),
    }))

    useActions(() => {
        return readMode ? {
            edit: () => setReadmode(false),
        } : {
            save: () => {
                dispatch(PgsseAction.updateIndicators({ indicators })).then(() => {
                    dispatch(PgsseAction.fetchIndicators())
                    setReadmode(true)
                })
            },
            cancel: () => {
                setReadmode(true)
                setIndicators(propsIndicators)
            },
        }
    }, [propsIndicators, indicators, readMode])

    const onValidate = () => {
        const updatedIndicator = { ...selectedIndicator, updateLogin: accountUser.login, updateDate: moment().valueOf() }
        if (!isNil(selectedIndicator.index)) {
            setIndicators(indexedIndicators.map((d, i) => i === selectedIndicator.index ? updatedIndicator : d))
        } else {
            setIndicators([ ...indexedIndicators, updatedIndicator ])
        }
        setIsOpen(false)
    }

    const actions = compact([
        !readMode && {
            icon: 'note_add',
            onClick: () => {
                setIsOpen(true)
                setSelectedIndicator({})
            },
        },
    ])

    const lineActions = [{
        icon: 'close',
        onClick: (element) => setIndicators(indexedIndicators.filter(l => l.index !== element.index)),
        displayed: !readMode,
    }, {
        icon: 'edit',
        onClick: (element) => {
            setSelectedIndicator(indexedIndicators.find(l => element.index === l.index))
            setIsOpen(true)
        },
        displayed: !readMode,
    }]

    return (
        <Box sx={{ padding: '10 10 60 20' }}>
            <CardTable
                title={i18n.indicators}
                rows={tableData}
                headers={['name', 'description', 'order', 'updateDate', 'updateLogin']}
                actions={actions}
                lineActions={lineActions}
            />
            <IndicatorDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                selectedIndicator={selectedIndicator}
                setSelectedIndicator={setSelectedIndicator}
                onValidate={onValidate}
            />
        </Box>
    )
}

export default PgsseSettings
